import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Spinner from 'react-bootstrap/Spinner'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { db } from '../../firebase'

const normalizeList = (items = []) => {
  if (Array.isArray(items)) {
    return items.map((item) => String(item || '').trim()).filter(Boolean)
  }

  if (typeof items === 'string') {
    return items.split(/\r?\n/).map((item) => item.trim()).filter(Boolean)
  }

  return []
}

const normalizeDailyPlan = (days = []) => {
  const labels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday']
  if (!Array.isArray(days)) return []

  return days.map((day, index) => {
    if (typeof day === 'string') {
      return {
        day: `Day ${index + 1}`,
        label: labels[index] || '',
        focus: day,
      }
    }

    return {
      day: day?.day || `Day ${index + 1}`,
      label: day?.label || day?.weekday || day?.date || labels[index] || '',
      focus: day?.focus || day?.topic || day?.activities || day?.description || 'Daily activity will be added.',
      teachingAid: day?.teachingAid || day?.aid || '',
      reference: day?.reference || '',
    }
  })
}

const normalizeScheme = (value = []) => {
  if (Array.isArray(value)) {
    return value.map((item, index) => {
      if (typeof item === 'string') {
        return {
          week: String(index + 1),
          topic: item,
          objectives: 'Objectives will be added.',
          activities: 'Learning activities will be added.',
          teachingAid: 'Teaching aids will be added.',
          reference: 'References will be added.',
          dailyPlan: [],
          id: `week-${index + 1}-${item}`,
        }
      }

      return {
        week: item?.week || String(index + 1),
        topic: item?.topic || `Week ${index + 1}`,
        objectives: item?.objectives || item?.objective || 'Objectives will be added.',
        activities: item?.activities || item?.learningActivities || 'Learning activities will be added.',
        teachingAid: item?.teachingAid || item?.aid || 'Teaching aids will be added.',
        reference: item?.reference || 'References will be added.',
        dailyPlan: normalizeDailyPlan(item?.dailyPlan || item?.days || item?.weekDays || item?.dailyBreakdown),
        id: item?.id || `week-${index + 1}-${item?.topic || `Week ${index + 1}`}`,
      }
    })
  }

  if (typeof value === 'string') {
    return value.split(/\r?\n/).map((item, index) => ({
      week: String(index + 1),
      topic: item.trim(),
      objectives: 'Objectives will be added.',
      activities: 'Learning activities will be added.',
      teachingAid: 'Teaching aids will be added.',
      reference: 'References will be added.',
      dailyPlan: [],
      id: `week-${index + 1}-${item.trim()}`,
    })).filter((item) => item.topic)
  }

  return []
}

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return 'Not set'
  const amount = Number(String(value).replace(/,/g, ''))
  if (Number.isNaN(amount)) return value
  return `KES ${new Intl.NumberFormat().format(amount)}`
}

const schemeImportTemplate = `{
  "overview": [
    "Build strong foundations in HTML, CSS, JavaScript, React, APIs, databases, and deployment.",
    "Practice weekly through guided labs, live class exercises, code reviews, and portfolio projects."
  ],
  "deliverables": [
    "Responsive student dashboard interface",
    "React components connected to live data",
    "Final portfolio project and presentation"
  ],
  "schemeOfWork": [
    {
      "week": "1",
      "topic": "Web foundations and developer workflow",
      "objectives": "Set up the development environment and understand how modern web apps are structured.",
      "activities": "Class walkthrough, Git practice, folder structure review, and first responsive page.",
      "teachingAid": "VS Code, GitHub, browser dev tools",
      "reference": "MDN Web Docs, class notes",
      "dailyPlan": [
        {
          "day": "Day 1",
          "label": "Monday",
          "focus": "Orientation, course outcomes, tools setup, and folder structure.",
          "teachingAid": "VS Code, GitHub",
          "reference": "Class notes"
        },
        {
          "day": "Day 2",
          "label": "Tuesday",
          "focus": "HTML structure, semantic tags, and building the first page section."
        },
        {
          "day": "Day 3",
          "label": "Wednesday",
          "focus": "CSS basics, spacing, color, typography, and browser inspection."
        },
        {
          "day": "Day 4",
          "label": "Thursday",
          "focus": "Git workflow, class recap, mini task, and gap review."
        }
      ]
    }
  ]
}`

const getFieldValue = (lines, labels) => {
  const match = lines.find((line) => labels.some((label) => line.toLowerCase().startsWith(`${label}:`)))
  if (!match) return ''
  return match.slice(match.indexOf(':') + 1).trim()
}

const extractJsonPaste = (text) => {
  const withoutFenceLabels = text
    .replace(/```(?:json)?/gi, '')
    .replace(/```/g, '')
    .trim()
  const firstObject = withoutFenceLabels.indexOf('{')
  const firstArray = withoutFenceLabels.indexOf('[')
  const starts = [firstObject, firstArray].filter((index) => index >= 0)

  if (!starts.length) return withoutFenceLabels

  const start = Math.min(...starts)
  const endChar = withoutFenceLabels[start] === '{' ? '}' : ']'
  const end = withoutFenceLabels.lastIndexOf(endChar)

  if (end <= start) return withoutFenceLabels

  return withoutFenceLabels.slice(start, end + 1).trim()
}

const parsePlainScheme = (text) => {
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
  const weekBlocks = []
  let activeBlock = null

  lines.forEach((line) => {
    const weekMatch = line.match(/^week\s*(\d+)\s*[:\-–]?\s*(.*)$/i)
    if (weekMatch) {
      if (activeBlock) weekBlocks.push(activeBlock)
      activeBlock = {
        week: weekMatch[1],
        header: weekMatch[2],
        lines: [],
      }
      return
    }

    if (activeBlock) activeBlock.lines.push(line)
  })

  if (activeBlock) weekBlocks.push(activeBlock)
  if (!weekBlocks.length) return []

  return weekBlocks.map((block, index) => {
    const topicFromField = getFieldValue(block.lines, ['topic'])
    const objectives = getFieldValue(block.lines, ['objectives', 'objective'])
    const activities = getFieldValue(block.lines, ['activities', 'class activities', 'learning activities'])
    const teachingAid = getFieldValue(block.lines, ['teaching aid', 'teaching aids', 'aid'])
    const reference = getFieldValue(block.lines, ['reference', 'references'])

    const dailyPlan = block.lines.map((line) => {
      const dayMatch = line.match(/^day\s*(\d+)\s*(?:\(([^)]+)\)|[:\-–]\s*([A-Za-z]+))?\s*[:\-–]\s*(.+)$/i)
      const weekdayMatch = line.match(/^(monday|tuesday|wednesday|thursday)\s*[:\-–]\s*(.+)$/i)

      if (dayMatch) {
        const dayNumber = dayMatch[1]
        return {
          day: `Day ${dayNumber}`,
          label: dayMatch[2] || dayMatch[3] || ['Monday', 'Tuesday', 'Wednesday', 'Thursday'][Number(dayNumber) - 1] || '',
          focus: dayMatch[4],
        }
      }

      if (weekdayMatch) {
        const label = weekdayMatch[1][0].toUpperCase() + weekdayMatch[1].slice(1).toLowerCase()
        const dayIndex = ['Monday', 'Tuesday', 'Wednesday', 'Thursday'].indexOf(label)
        return {
          day: `Day ${dayIndex + 1}`,
          label,
          focus: weekdayMatch[2],
        }
      }

      return null
    }).filter(Boolean)

    return {
      week: block.week || String(index + 1),
      topic: topicFromField || block.header || `Week ${block.week || index + 1}`,
      objectives: objectives || 'Objectives will be added.',
      activities: activities || 'Learning activities will be added.',
      teachingAid: teachingAid || 'Teaching aids will be added.',
      reference: reference || 'References will be added.',
      dailyPlan,
      id: `week-${block.week || index + 1}-${topicFromField || block.header || 'scheme'}`,
    }
  })
}

const parseSchemeImport = (text) => {
  const trimmed = text.trim()
  if (!trimmed) {
    throw new Error('Paste a curriculum or scheme before previewing.')
  }

  const possibleJson = extractJsonPaste(trimmed)

  try {
    const parsed = JSON.parse(possibleJson)
    const schemeSource = Array.isArray(parsed)
      ? parsed
      : parsed.schemeOfWork || parsed.scheme || parsed.courseScheme || parsed.schemeTemplate || []

    return {
      curriculumUrl: Array.isArray(parsed) ? '' : String(parsed.curriculumUrl || parsed.courseUrl || parsed.sourceUrl || '').trim(),
      overview: Array.isArray(parsed) ? [] : normalizeList(parsed.overview || parsed.curriculumOverview || parsed.courseOverview),
      deliverables: Array.isArray(parsed) ? [] : normalizeList(parsed.deliverables || parsed.courseDeliverables),
      schemeOfWork: normalizeScheme(schemeSource),
    }
  } catch (jsonError) {
    const schemeOfWork = normalizeScheme(parsePlainScheme(trimmed))
    if (!schemeOfWork.length) {
      throw new Error('Could not read the paste. Use the JSON template or plain text with headings like "Week 1" and "Day 1: ...".')
    }

    return {
      curriculumUrl: '',
      overview: [],
      deliverables: [],
      schemeOfWork,
    }
  }
}

function CourseDetailsComp() {
  const { courseId } = useParams()
  const navigate = useNavigate()
  const [course, setCourse] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [curriculumUrl, setCurriculumUrl] = useState('')
  const [bulkSchemeText, setBulkSchemeText] = useState('')
  const [importPreview, setImportPreview] = useState(null)
  const [importMessage, setImportMessage] = useState('')
  const [savingScheme, setSavingScheme] = useState(false)

  useEffect(() => {
    const loadCourse = async () => {
      if (!courseId) {
        setLoading(false)
        setError('No course was selected.')
        return
      }

      setLoading(true)
      setError('')

      try {
        const courseSnap = await getDoc(doc(db, 'courses', courseId))
        if (!courseSnap.exists()) {
          setCourse(null)
          setError('Course not found.')
          return
        }

        setCourse({ id: courseSnap.id, ...courseSnap.data() })
      } catch (loadError) {
        setError(loadError?.message || 'Could not load this course.')
      } finally {
        setLoading(false)
      }
    }

    loadCourse()
  }, [courseId])

  useEffect(() => {
    setCurriculumUrl(course?.curriculumUrl || course?.courseUrl || course?.sourceUrl || '')
  }, [course])

  const overview = useMemo(
    () => normalizeList(course?.overview || course?.curriculumOverview || course?.courseOverview),
    [course]
  )
  const deliverables = useMemo(
    () => normalizeList(course?.deliverables || course?.courseDeliverables),
    [course]
  )
  const schemeItems = useMemo(
    () => normalizeScheme(course?.schemeOfWork || course?.scheme || course?.courseScheme || course?.schemeTemplate),
    [course]
  )

  const previewImport = () => {
    try {
      const nextPreview = parseSchemeImport(bulkSchemeText)
      if (nextPreview.curriculumUrl && !curriculumUrl.trim()) {
        setCurriculumUrl(nextPreview.curriculumUrl)
      }
      setImportPreview(nextPreview)
      setImportMessage(`Ready to save ${nextPreview.schemeOfWork.length} week${nextPreview.schemeOfWork.length === 1 ? '' : 's'}.`)
    } catch (previewError) {
      setImportPreview(null)
      setImportMessage(previewError.message)
    }
  }

  const saveImportedScheme = async () => {
    if (!course?.id) return

    let nextPreview = importPreview
    if (!nextPreview) {
      try {
        nextPreview = parseSchemeImport(bulkSchemeText)
      } catch (previewError) {
        setImportMessage(previewError.message)
        return
      }
    }

    setSavingScheme(true)
    setImportMessage('')

    try {
      const payload = {
        curriculumUrl: curriculumUrl.trim() || nextPreview.curriculumUrl || '',
        schemeOfWork: nextPreview.schemeOfWork,
        updatedAt: Date.now(),
      }

      if (nextPreview.overview.length) payload.overview = nextPreview.overview
      if (nextPreview.deliverables.length) payload.deliverables = nextPreview.deliverables

      await updateDoc(doc(db, 'courses', course.id), payload)
      setCourse((current) => ({ ...current, ...payload }))
      setImportPreview(nextPreview)
      setImportMessage(`Saved ${nextPreview.schemeOfWork.length} week${nextPreview.schemeOfWork.length === 1 ? '' : 's'} to this course.`)
    } catch (saveError) {
      setImportMessage(saveError?.message || 'Could not save this scheme of work.')
    } finally {
      setSavingScheme(false)
    }
  }

  if (loading) {
    return (
      <div className='adminCourseDetailsPage'>
        <div className='adminCourseLoading'>
          <Spinner animation='border' size='sm' />
          <span>Loading course details...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='adminCourseDetailsPage'>
      <section className='adminCourseHero'>
        <button type='button' className='adminCourseBackBtn' onClick={() => navigate('/courses')}>
          Back to Courses
        </button>
        <span className='adminCourseTag'>Course Details</span>
        <h1>{course?.mcourseName || 'Course Details'}</h1>
        <p>{course?.mcourseDescription || course?.description || 'No course description has been added yet.'}</p>
      </section>

      {error ? <p className='adminCourseError'>{error}</p> : null}

      {course ? (
        <>
          <section className='adminCourseStats'>
            <div>
              <span>Duration</span>
              <strong>{course.mcourseDuration || `${course.courseDuration || 'Not set'} months`}</strong>
            </div>
            <div>
              <span>Total Fee</span>
              <strong>{formatAmount(course.mcoursePrice)}</strong>
            </div>
            <div>
              <span>Deposit</span>
              <strong>{formatAmount(course.feeDeposit)}</strong>
            </div>
            <div>
              <span>Installment</span>
              <strong>{formatAmount(course.feeInstallments)}</strong>
            </div>
          </section>

          <section className='adminCoursePanel adminSchemeBuilder'>
            <div className='adminCourseSectionHead adminCourseSchemeHead'>
              <div>
                <span className='adminCourseTag'>Scheme Builder</span>
                <h2>Paste curriculum, generate structure, save once</h2>
                <p className='adminCourseText'>
                  Add the source curriculum link, then paste a structured scheme. JSON gives the cleanest result;
                  plain text also works when it uses Week and Day labels.
                </p>
              </div>
              <button
                type='button'
                className='adminCourseGhostBtn'
                onClick={() => {
                  setBulkSchemeText(schemeImportTemplate)
                  setImportPreview(null)
                  setImportMessage('Template loaded. Edit it, then preview before saving.')
                }}
              >
                Load JSON template
              </button>
            </div>

            <label className='adminCourseField'>
              <span>Curriculum source link</span>
              <input
                type='url'
                value={curriculumUrl}
                onChange={(event) => setCurriculumUrl(event.target.value)}
                placeholder='https://ubunifucollege.com/curriculum/software-engineering'
              />
            </label>

            <label className='adminCourseField'>
              <span>Bulk curriculum / scheme paste</span>
              <textarea
                value={bulkSchemeText}
                onChange={(event) => {
                  setBulkSchemeText(event.target.value)
                  setImportPreview(null)
                  setImportMessage('')
                }}
                rows={14}
                placeholder={'Paste JSON from the template, or plain text like:\nWeek 1: Web foundations\nObjectives: Set up tools and understand web structure.\nDay 1: Orientation and tools setup\nDay 2: HTML structure\nDay 3: CSS basics\nDay 4: Git workflow and recap'}
              />
            </label>

            <div className='adminSchemeBuilderActions'>
              <button type='button' className='adminCourseGhostBtn' onClick={previewImport}>
                Preview import
              </button>
              <button
                type='button'
                className='adminCoursePrimaryBtn'
                onClick={saveImportedScheme}
                disabled={savingScheme}
              >
                {savingScheme ? 'Saving scheme...' : 'Save scheme to course'}
              </button>
            </div>

            {importMessage ? <p className='adminCourseImportMessage'>{importMessage}</p> : null}

            {importPreview ? (
              <div className='adminSchemePreview'>
                <div>
                  <span>Overview items</span>
                  <strong>{importPreview.overview.length}</strong>
                </div>
                <div>
                  <span>Deliverables</span>
                  <strong>{importPreview.deliverables.length}</strong>
                </div>
                <div>
                  <span>Weeks</span>
                  <strong>{importPreview.schemeOfWork.length}</strong>
                </div>
                <div>
                  <span>Daily activities</span>
                  <strong>{importPreview.schemeOfWork.reduce((sum, week) => sum + (week.dailyPlan?.length || 0), 0)}</strong>
                </div>
              </div>
            ) : null}
          </section>

          <section className='adminCourseGrid'>
            <article className='adminCoursePanel'>
              <div className='adminCourseSectionHead'>
                <span className='adminCourseTag'>Overview</span>
                <h2>Curriculum overview</h2>
              </div>
              {overview.length ? (
                <ul className='adminCourseList'>
                  {overview.map((item) => <li key={item}>{item}</li>)}
                </ul>
              ) : (
                <p className='adminCourseText'>No curriculum overview has been added yet.</p>
              )}
            </article>

            <article className='adminCoursePanel'>
              <div className='adminCourseSectionHead'>
                <span className='adminCourseTag'>Deliverables</span>
                <h2>What students should produce</h2>
              </div>
              {deliverables.length ? (
                <ul className='adminCourseList'>
                  {deliverables.map((item) => <li key={item}>{item}</li>)}
                </ul>
              ) : (
                <p className='adminCourseText'>No course deliverables have been added yet.</p>
              )}
            </article>
          </section>

          <section className='adminCoursePanel adminCoursePanelFull'>
            <div className='adminCourseSectionHead adminCourseSchemeHead'>
              <div>
                <span className='adminCourseTag'>Scheme of Work</span>
                <h2>Weekly plan and daily activities</h2>
                <p className='adminCourseText'>Each week can include Day 1 to Day 4 for Monday to Thursday classes.</p>
              </div>
              <strong>{schemeItems.length} weeks</strong>
            </div>

            {schemeItems.length ? (
              <div className='adminSchemeList'>
                {schemeItems.map((item) => (
                  <article key={item.id || item.topic} className='adminSchemeWeek'>
                    <div className='adminSchemeWeekTop'>
                      <span>Week {item.week}</span>
                      <div>
                        <h3>{item.topic}</h3>
                        <p>{item.objectives}</p>
                      </div>
                    </div>

                    {item.dailyPlan?.length ? (
                      <div className='adminSchemeTableWrap'>
                        <table className='adminSchemeTable'>
                          <thead>
                            <tr>
                              <th>Day</th>
                              <th>Class Day</th>
                              <th>Daily Activity</th>
                              <th>Teaching Aid</th>
                              <th>Reference</th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.dailyPlan.map((day) => (
                              <tr key={`${item.id}-${day.day}-${day.label}`}>
                                <td>{day.day}</td>
                                <td>{day.label}</td>
                                <td>{day.focus}</td>
                                <td>{day.teachingAid || item.teachingAid}</td>
                                <td>{day.reference || item.reference}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className='adminCourseText'>No daily activity table has been added for this week.</p>
                    )}

                    <div className='adminSchemeMeta'>
                      <div>
                        <span>Class Activities</span>
                        <p>{item.activities}</p>
                      </div>
                      <div>
                        <span>Teaching Aid</span>
                        <p>{item.teachingAid}</p>
                      </div>
                      <div>
                        <span>Reference</span>
                        <p>{item.reference}</p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <p className='adminCourseText'>No scheme of work has been added yet.</p>
            )}
          </section>
        </>
      ) : null}
    </div>
  )
}

export default CourseDetailsComp
